import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

// class HeaderComponent extends Component {
//   requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

//   /**
//    * Width of window when header drawer was hidden
//    * @type {number | null}
//    */
//   #menuDrawerHiddenWidth = null;

//   /**
//    * An intersection observer for monitoring sticky header position
//    * @type {IntersectionObserver | null}
//    */
//   #intersectionObserver = null;

//   /**
//    * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
//    * @type {boolean}
//    */
//   #offscreen = false;

//   /**
//    * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
//    * @type {number}
//    */
//   #lastScrollTop = 0;

//   /**
//    * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
//    * @type {number | null}
//    */
//   #timeout = null;

//   /**
//    * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
//    * @constant {number}
//    */
//   #animationDelay = 150;

//   /**
//    * Keeps the global `--header-height` custom property up to date,
//    * which other theme components can then consume
//    */
//   #resizeObserver = new ResizeObserver(([entry]) => {
//     if (!entry) return;

//     const { height } = entry.target.getBoundingClientRect();
//     document.body.style.setProperty('--header-height', `${height}px`);

//     // Check if the menu drawer should be hidden in favor of the header menu
//     if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
//       this.#updateMenuVisibility(false);
//     }
//   });

//   /**
//    * Observes the header while scrolling the viewport to track when its actively sticky
//    * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
//    */
//   #observeStickyPosition = (alwaysSticky = true) => {
//     if (this.#intersectionObserver) return;

//     const config = {
//       threshold: alwaysSticky ? 1 : 0,
//     };

//     this.#intersectionObserver = new IntersectionObserver(([entry]) => {
//       if (!entry) return;

//       const { isIntersecting } = entry;

//       if (alwaysSticky) {
//         this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
//         changeMetaThemeColor(this.refs.headerRowTop);
//       } else {
//         this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
//       }
//     }, config);

//     this.#intersectionObserver.observe(this);
//   };

//   /**
//    * Handles the overflow minimum event from the header menu
//    * @param {OverflowMinimumEvent} event
//    */
//   #handleOverflowMinimum = (event) => {
//     this.#updateMenuVisibility(event.detail.minimumReached);
//   };

//   /**
//    * Updates the visibility of the menu and drawer
//    * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
//    */
//   #updateMenuVisibility(hideMenu) {
//     if (hideMenu) {
//       this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
//       this.#menuDrawerHiddenWidth = window.innerWidth;
//       this.refs.headerMenu.classList.add('hidden');
//     } else {
//       this.refs.headerDrawerContainer.classList.add('desktop:hidden');
//       this.#menuDrawerHiddenWidth = null;
//       this.refs.headerMenu.classList.remove('hidden');
//     }
//   }

//   #handleWindowScroll = () => {
//     const stickyMode = this.getAttribute('sticky');
//     if (!this.#offscreen && stickyMode !== 'always') return;

//     const scrollTop = document.scrollingElement?.scrollTop ?? 0;
//     const isScrollingUp = scrollTop < this.#lastScrollTop;
//     if (this.#timeout) {
//       clearTimeout(this.#timeout);
//       this.#timeout = null;
//     }

//     if (stickyMode === 'always') {
//       const isAtTop = this.getBoundingClientRect().top >= 0;

//       if (isAtTop) {
//         this.dataset.scrollDirection = 'none';
//       } else if (isScrollingUp) {
//         this.dataset.scrollDirection = 'up';
//       } else {
//         this.dataset.scrollDirection = 'down';
//       }

//       this.#lastScrollTop = scrollTop;
//       return;
//     }

//     if (isScrollingUp) {
//       this.removeAttribute('data-animating');

//       if (this.getBoundingClientRect().top >= 0) {
//         // reset sticky state when header is scrolled up to natural position
//         this.#offscreen = false;
//         this.dataset.stickyState = 'inactive';
//         this.dataset.scrollDirection = 'none';
//       } else {
//         // show sticky header when scrolling up
//         this.dataset.stickyState = 'active';
//         this.dataset.scrollDirection = 'up';
//       }
//     } else if (this.dataset.stickyState === 'active') {
//       this.dataset.scrollDirection = 'none';
//       // delay transitioning to idle hidden state for hiding animation
//       this.setAttribute('data-animating', '');

//       this.#timeout = setTimeout(() => {
//         this.dataset.stickyState = 'idle';
//         this.removeAttribute('data-animating');
//       }, this.#animationDelay);
//     } else {
//       this.dataset.scrollDirection = 'none';
//       this.dataset.stickyState = 'idle';
//     }

//     this.#lastScrollTop = scrollTop;
//   };

//   connectedCallback() {
//     super.connectedCallback();
//     this.#resizeObserver.observe(this);
//     this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

//     const stickyMode = this.getAttribute('sticky');
//     if (stickyMode) {
//       this.#observeStickyPosition(stickyMode === 'always');

//       if (stickyMode === 'scroll-up' || stickyMode === 'always') {
//         document.addEventListener('scroll', this.#handleWindowScroll);
//       }
//     }
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#resizeObserver.disconnect();
//     this.#intersectionObserver?.disconnect();
//     this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
//     document.removeEventListener('scroll', this.#handleWindowScroll);
//     document.body.style.setProperty('--header-height', '0px');
//   }
// }

// if (!customElements.get('header-component')) {
//   customElements.define('header-component', HeaderComponent);
// }

// onDocumentLoaded(() => {
//   const header = document.querySelector('#header-component');
//   const headerGroup = document.querySelector('#header-group');

//   // Update header group height on resize of any child
//   if (headerGroup) {
//     const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

//     // Observe all children of the header group
//     const children = headerGroup.children;
//     for (let i = 0; i < children.length; i++) {
//       const element = children[i];
//       if (element === header || !(element instanceof HTMLElement)) continue;
//       resizeObserver.observe(element);
//     }

//     // Also observe the header group itself for child changes
//     const mutationObserver = new MutationObserver((mutations) => {
//       for (const mutation of mutations) {
//         if (mutation.type === 'childList') {
//           // Re-observe all children when the list changes
//           const children = headerGroup.children;
//           for (let i = 0; i < children.length; i++) {
//             const element = children[i];
//             if (element === header || !(element instanceof HTMLElement)) continue;
//             resizeObserver.observe(element);
//           }
//         }
//       }
//     });

//     mutationObserver.observe(headerGroup, { childList: true });
//   }
// });




// document.addEventListener("DOMContentLoaded", function() {
//   // Hide all submenus initially
//   document.querySelectorAll(".sub-menu").forEach(sub => {
//     sub.style.display = "none";
//   });

//   // Delegate clicks on all menu links
//   document.querySelector(".menu").addEventListener("click", function(e) {
//     const link = e.target.closest(".menu-link");
//     if (!link) return;

//     const parentItem = link.closest(".menu-item");
//     const submenu = parentItem ? parentItem.querySelector(".sub-menu") : null;
//     const href = link.getAttribute("href");
//     const isInvalidHref = !href || href === "#" || href.trim() === "" || href === "/";

//     // If the link has a submenu, always prevent default to toggle
//     if (submenu) {
//       e.preventDefault();

//       const isExpanded = submenu.classList.contains("expanded");
//       if (isExpanded) {
//         submenu.classList.remove("expanded");
//         submenu.style.display = "none";
//       } else {
//         submenu.classList.add("expanded");
//         submenu.style.display = "block";
//       }
//       return; // stop further processing
//     }

//     // If no submenu but invalid href, prevent navigation
//     if (isInvalidHref) {
//       e.preventDefault();
//     }
//   });
// });

// document.addEventListener("DOMContentLoaded", function () {
//   // Hide all submenus initially
//   document.querySelectorAll(".sub-menu").forEach(sub => {
//     sub.style.display = "none";
//   });

//   // Function to close all submenus
//   function closeAllSubmenus(except = null) {
//     document.querySelectorAll(".sub-menu.expanded").forEach(openSub => {
//       if (openSub !== except) {
//         openSub.classList.remove("expanded");
//         openSub.style.display = "none";
//       }
//     });
//   }

//   // Attach event listeners to ALL menus
//   document.querySelectorAll(".menu").forEach(menu => {
//     menu.addEventListener("click", function (e) {
//       const link = e.target.closest(".menu-link");
//       if (!link) return;

//       const parentItem = link.closest(".menu-item");
//       const submenu = parentItem ? parentItem.querySelector(".sub-menu") : null;
//       const href = link.getAttribute("href");
//       const isInvalidHref = !href || href === "#" || href.trim() === "" || href === "/";

//       if (submenu) {
//         e.preventDefault();

//         const isExpanded = submenu.classList.contains("expanded");

//         // Close all others first
//         closeAllSubmenus(submenu);

//         if (isExpanded) {
//           submenu.classList.remove("expanded");
//           submenu.style.display = "none";
//         } else {
//           submenu.classList.add("expanded");
//           submenu.style.display = "block";
//         }

//         return;
//       }

//       if (isInvalidHref) {
//         e.preventDefault();
//       }
//     });

//     // Hover support (desktop only)
//     menu.querySelectorAll(".menu-item").forEach(item => {
//       const submenu = item.querySelector(".sub-menu");
//       if (submenu) {
//         item.addEventListener("mouseenter", () => {
//           if (window.innerWidth > 768) { // desktop only
//             closeAllSubmenus(submenu);
//             submenu.classList.add("expanded");
//             submenu.style.display = "block";
//           }
//         });

//         item.addEventListener("mouseleave", () => {
//           if (window.innerWidth > 768) { // desktop only
//             submenu.classList.remove("expanded");
//             submenu.style.display = "none";
//           }
//         });
//       }
//     });
//   });

//   // Close when clicking outside
//   document.addEventListener("click", function (e) {
//     if (!e.target.closest(".menu")) {
//       closeAllSubmenus();
//     }
//   });
// });


// document.addEventListener("DOMContentLoaded", function () {
//   const menuItems = document.querySelectorAll(".menu-item");

//   menuItems.forEach(item => {
//     const trigger = item.querySelector(".menu-link");
//     const submenu = item.querySelector(".sub-menu");

//     if (!submenu || !trigger) return; // skip items without submenu

//     // Hover (desktop)
//     item.addEventListener("mouseenter", () => {
//       submenu.classList.add("expanded");
//     });

//     item.addEventListener("mouseleave", () => {
//       submenu.classList.remove("expanded");
//     });

//     // Click (mobile & desktop fallback)
//     trigger.addEventListener("click", (e) => {
//       e.preventDefault(); // prevent accidental nav if turned into <a>
//       // Toggle submenu
//       submenu.classList.toggle("expanded");
//     });
//   });

//   // Close any submenu if click happens outside
//   document.addEventListener("click", function (e) {
//     document.querySelectorAll(".sub-menu.expanded").forEach(openMenu => {
//       if (!openMenu.closest(".menu-item").contains(e.target)) {
//         openMenu.classList.remove("expanded");
//       }
//     });
//   });
// });


document.addEventListener("DOMContentLoaded", function () {
  const hamburger = document.querySelector(".hamburger");
  const menus = document.querySelectorAll(".menu");
  const menuItems = document.querySelectorAll(".menu-item");
  const appHeader = document.querySelector(".app-header");

  // -------------------------------
  // Update body --announceHeight
  // -------------------------------
  const updateAnnounceHeight = () => {
    const announcement = document.querySelector(".app-announcement");
    if (announcement) {
      const height = announcement.offsetHeight;
      document.body.style.setProperty("--announceHeight", height + "px");
    } else {
      document.body.style.setProperty("--announceHeight", "0px");
    }
  };

  // Run once and on resize
  // Initialize to 0px first to avoid jump
  document.body.style.setProperty("--announceHeight", "0px");
  updateAnnounceHeight();
  window.addEventListener("resize", updateAnnounceHeight);


  // -------------------------------
  // Hamburger toggle (mobile)
  // -------------------------------
  if (hamburger) {
    hamburger.addEventListener("click", () => {
      const isOpen = hamburger.getAttribute("aria-expanded") === "true";
      hamburger.setAttribute("aria-expanded", !isOpen);

      menus.forEach(menu => menu.classList.toggle("active"));

      // Toggle body class for scroll lock
      document.body.classList.toggle("menu-expanded", !isOpen);
      if (appHeader) {
        appHeader.classList.toggle("expanded", !isOpen);
      }
    });
  }

  // -------------------------------
  // Submenu handling
  // -------------------------------
  menuItems.forEach(item => {
    const trigger = item.querySelector(".menu-link");
    const submenu = item.querySelector(".sub-menu");
    if (!submenu || !trigger) return;

    // Desktop → open on hover
    item.addEventListener("mouseenter", () => {
      if (window.innerWidth > 768) {
        submenu.classList.add("expanded");
      }
    });

    item.addEventListener("mouseleave", () => {
      if (window.innerWidth > 768) {
        submenu.classList.remove("expanded");
      }
    });

    // Mobile → open on click
    trigger.addEventListener("click", (e) => {
      if (window.innerWidth <= 768) {
        e.preventDefault();
        // Close other submenus first
        document.querySelectorAll(".sub-menu.expanded").forEach(openMenu => {
          if (openMenu !== submenu) openMenu.classList.remove("expanded");
        });
        submenu.classList.toggle("expanded");
      }
    });
  });

  // -------------------------------
  // Close menu when clicking outside
  // -------------------------------
  document.addEventListener("click", (e) => {
    const clickedInsideMenu = e.target.closest(".menu, .hamburger");

    // Mobile close
    if (window.innerWidth <= 768 && !clickedInsideMenu) {
      menus.forEach(menu => menu.classList.remove("active"));
      if (hamburger) hamburger.setAttribute("aria-expanded", "false");
      document.body.classList.remove("menu-expanded");
      document.querySelectorAll(".sub-menu.expanded").forEach(openMenu => {
        openMenu.classList.remove("expanded");
      });
    }

    // Desktop close submenus only
    if (window.innerWidth > 768 && !clickedInsideMenu) {
      document.querySelectorAll(".sub-menu.expanded").forEach(openMenu => {
        openMenu.classList.remove("expanded");
      });
    }
  });

  // -------------------------------
  // Keyboard support (Escape closes)
  // -------------------------------
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      menus.forEach(menu => menu.classList.remove("active"));
      if (hamburger) hamburger.setAttribute("aria-expanded", "false");
      document.body.classList.remove("menu-expanded");
      document.querySelectorAll(".sub-menu.expanded").forEach(openMenu => {
        openMenu.classList.remove("expanded");
      });
    }
  });
});


// (function() {
//   'use strict';

//   // Search functionality
//   const HeaderSearch = {
//     init: function() {
//       this.searchExpanded = false;
//       this.searchText = '';
//       this.bindEvents();
//       // this.setupSearchStyles();
//       this.cartOpen = false;
//     },

//     bindEvents: function() {
//       const self = this;

//       // Search click handler
//       document.addEventListener('click', function(e) {
//         if (e.target.closest('.menu-item-label.search')) {
//           e.preventDefault();
//           self.handleSearchClick();
//         }
//       });

//       // Cart click handler
//       document.addEventListener('click', function(e) {
//         if (e.target.closest('.menu-item-label.cart') || e.target.closest('.cart-item a')) {
//           e.preventDefault();
//           self.handleCartClick();
//         }
//       });

//       // Search input focus/blur
//       document.addEventListener('focus', function(e) {
//         if (e.target.id === 'search-input') {
//           self.expandSearch();
//         }
//       }, true);

//       document.addEventListener('blur', function(e) {
//         if (e.target.id === 'search-input') {
//           // Small delay to allow form submission
//           setTimeout(function() {
//             if (!self.searchText.trim()) {
//               self.collapseSearch();
//             }
//           }, 150);
//         }
//       }, true);

//       // Search input change
//       document.addEventListener('input', function(e) {
//         if (e.target.id === 'search-input') {
//           self.searchText = e.target.value;
//         }
//       });

//       // Search form submission
//       document.addEventListener('submit', function(e) {
//         if (e.target.classList.contains('searchForm')) {
//           const query = document.getElementById('search-input').value.trim();
//           if (query.length === 0) {
//             e.preventDefault();
//             return false;
//           }
//           self.handleSearch(query);
//         }
//       });

//       // Handle escape key
//       document.addEventListener('keydown', function(e) {
//         if (e.target.id === 'search-input' && e.key === 'Escape') {
//           self.collapseSearch();
//           e.target.blur();
//         }
//       });

//       // Handle enter key
//       document.addEventListener('keydown', function(e) {
//         if (e.target.id === 'search-input' && e.key === 'Enter') {
//           const query = e.target.value.trim();
//           if (query.length > 0) {
//             self.handleSearch(query);
//           }
//         }
//       });
//       // Close cart when clicking outside
//       document.addEventListener('click', function(e) {
//         if (self.cartOpen && !e.target.closest('.cart-item') && !e.target.closest('.shopping-cart')) {
//           self.closeCart();
//         }
//       });
//     },

//     handleSearchClick: function() {
//       if (this.searchExpanded && this.searchText.length > 0) {
//         this.handleSearch(this.searchText);
//       } else {
//         this.toggleSearch();
//       }
//     },

//     handleCartClick: function() {
//       if (this.cartOpen) {
//         this.closeCart();
//       } else {
//         this.openCart();
//       }
//     },

//     toggleSearch: function() {
//       this.searchExpanded = !this.searchExpanded;
//       if (this.searchExpanded) {
//         this.expandSearch();
//       } else {
//         this.collapseSearch();
//       }
//     },

//     expandSearch: function() {
//       this.searchExpanded = true;
//       const searchLabel = document.querySelector('.menu-item-label.search');
//       if (searchLabel) {
//         searchLabel.classList.add('expanded');
//       }
//       // Add expanded class to right menu items
//       const rightMenuItems = document.querySelectorAll('.menu.right-menu .menu-item[role="menuitem"]');
//       rightMenuItems.forEach(function(item) {
//         item.classList.add('expanded');
//       });
//       const searchInput = document.getElementById('search-input');
//       if (searchInput) {
//         searchInput.focus();
//       }
//     },

//     collapseSearch: function() {
//       this.searchExpanded = false;
//       this.searchText = '';
//       const searchLabel = document.querySelector('.menu-item-label.search');
//       if (searchLabel) {
//         searchLabel.classList.remove('expanded');
//       }
//       // Remove expanded class from right menu items
//       const rightMenuItems = document.querySelectorAll('.menu.right-menu .menu-item[role="menuitem"]');
//       rightMenuItems.forEach(function(item) {
//         item.classList.remove('expanded');
//       });
//       const searchInput = document.getElementById('search-input');
//       if (searchInput) {
//         searchInput.value = '';
//       }
//     },

//     openCart: function() {
//       this.cartOpen = true;
//       document.body.classList.add('cart-open');

//       // Open cart overlay
//       const overlay = document.querySelector('.cart-page-overlay');
//       const container = document.querySelector('.cart-container');

//       if (overlay) overlay.classList.add('overlay-open');
//       if (container) container.classList.add('open-panel');

//       // Prevent body scroll
//       document.body.style.overflow = 'hidden';


//       // Add cart-open class to cart item
//       const cartItem = document.querySelector('.cart-item');
//       if (cartItem) {
//         cartItem.classList.add('cart-open');
//       }

//       // Emit custom event for other components to listen to
//       document.dispatchEvent(new CustomEvent('cartOpened'));
//     },

//     closeCart: function() {
//       this.cartOpen = false;
//       document.body.classList.remove('cart-open');

//       // Close cart overlay
//       const overlay = document.querySelector('.cart-page-overlay');
//       const container = document.querySelector('.cart-container');

//       if (overlay) overlay.classList.remove('overlay-open');
//       if (container) container.classList.remove('open-panel');

//       // Restore body scroll
//       document.body.style.overflow = '';

//       // Remove cart-open class from cart item
//       const cartItem = document.querySelector('.cart-item');
//       if (cartItem) {
//         cartItem.classList.remove('cart-open');
//       }

//       // Emit custom event for other components to listen to
//       document.dispatchEvent(new CustomEvent('cartClosed'));
//     },

//     handleSearch: function(query) {
//       if (query && query.trim().length > 0) {
//         // Navigate to search page with query
//         window.location.href = '/search?q=' + encodeURIComponent(query.trim());
//       }
//     },

//     // setupSearchStyles: function() {
//     //   // Add CSS for search functionality
//     //   // const searchCSS = `
//     //   //   <style>
//     //   //     .menu-item-label.search {
//     //   //       display: flex;
//     //   //       align-items: center;
//     //   //       cursor: pointer;
//     //   //       transition: all 0.3s ease;
//     //   //       position: relative;
//     //   //     }

//     //   //     .menu-item-label.search .searchForm {
//     //   //       display: flex;
//     //   //       align-items: center;
//     //   //       width: 0;
//     //   //       overflow: hidden;
//     //   //       transition: width 0.3s ease;
//     //   //       margin-left: 8px;
//     //   //     }

//     //   //     .menu-item-label.search .search-input {
//     //   //       border: none;
//     //   //       border-bottom: 1px solid #646b6b;
//     //   //       background-color: transparent;
//     //   //       padding: 4px 0;
//     //   //       width: 0;
//     //   //       transition: width 0.3s ease;
//     //   //       color: #646b6b;
//     //   //       font-size: 14px;
//     //   //     }

//     //   //     .menu-item-label.search .search-input:focus {
//     //   //       outline: none;
//     //   //       border-bottom-width: 2px;
//     //   //     }

//     //   //     .menu-item-label.search .search-input::placeholder {
//     //   //       opacity: 0.7;
//     //   //       color: #646b6b;
//     //   //     }

//     //   //     .menu-item-label.search.expanded {
//     //   //       width: 100%;
//     //   //     }

//     //   //     .menu-item-label.search.expanded .searchForm {
//     //   //       width: 200px;
//     //   //     }

//     //   //     .menu-item-label.search.expanded .search-input {
//     //   //       width: 100%;
//     //   //       padding-left: 8px;
//     //   //     }

//     //   //     /* Mobile responsive */
//     //   //     @media (max-width: 768px) {
//     //   //       .menu-item-label.search.expanded .searchForm {
//     //   //         width: 150px;
//     //   //       }
//     //   //     }

//     //   //     @media (max-width: 480px) {
//     //   //       .menu-item-label.search.expanded .searchForm {
//     //   //         width: 120px;
//     //   //       }
//     //   //     }
//     //   //   </style>
//     //   // `;

//     //   // Check if styles already exist to avoid duplicates
//     //   if (!document.getElementById('header-search-styles')) {
//     //     const styleElement = document.createElement('div');
//     //     // styleElement.innerHTML = searchCSS;
//     //     styleElement.id = 'header-search-styles';
//     //     document.head.appendChild(styleElement.firstElementChild);
//     //   }
//     // }
//   };

//   // Initialize when DOM is ready
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', function() {
//       HeaderSearch.init();
//     });
//   } else {
//     HeaderSearch.init();
//   }

//   // Export for global access if needed
//   window.HeaderSearch = HeaderSearch;

// })();




// custom functions
// function openCart() {
//     //Mark cart as open
//     document.body.classList.add('cart-open');

//     //Open cart overlay
//     const overlay = document.querySelector('.cart-page-overlay');
//     const container = document.querySelector('.cart-container');

//     if (overlay) overlay.classList.add('overlay-open');
//     if (container) container.classList.add('open-panel');

//     //Prevent body scroll
//     document.body.style.overflow = 'hidden';

//     //Add cart-open class to cart item
//     const cartItem = document.querySelector('.cart-item');
//     if (cartItem) {
//         cartItem.classList.add('cart-open');
//     }   
// }
// function closeCart() {
//     document.body.classList.remove('cart-open');
//     document.body.style.overflow = ''; //restore scrolling

//     const overlay = document.querySelector('.cart-page-overlay');
//     const container = document.querySelector('.cart-container');
//     const cartItem = document.querySelector('.cart-item');

//     if (overlay) overlay.classList.remove('overlay-open');
//     if (container) container.classList.remove('open-panel');
//     if (cartItem) cartItem.classList.remove('cart-open');
// }

// //Example binding to close button
// const closeBtn = document.querySelector('.cart-close');
// if (closeBtn) {
//     closeBtn.addEventListener('click', closeCart);
// }



// document.querySelectorAll('.add-to-cart-form').forEach(form => {
//   form.addEventListener('submit', function(event) {
//     event.preventDefault();

//     const productId = this.dataset.productid;

//     fetch('/cart/add.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ items: [{ id: productId, quantity: 1 }] })
//     })
//     .then(res => res.json())
//     .then(() => fetch('/cart.js'))
//     .then(res => res.json())
//     .then(cart => {
//       //Update cart count
//       const cartCount = document.querySelector('.cart-count');
//       if (cartCount) cartCount.textContent = cart.item_count;

//       const itemsContainer = document.querySelector('.cart-items-container');
//       const emptyMessage = document.querySelector('.empty-cart-message');
//       const cartFooter = document.querySelector('.cart-footer');

//       if (!itemsContainer) return;

//       //Hide empty message if cart has items
//       if (emptyMessage) emptyMessage.style.display = cart.item_count > 0 ? 'none' : 'block';

//       //Keep existing markup: update existing items, append new ones
//       cart.items.forEach(item => {
//         let cartItemEl = document.querySelector(`.cart-item[data-item-key="${item.key}"]`);

//         if (cartItemEl) {
//           //Update existing item quantity and price
//           const qtyInput = cartItemEl.querySelector('input[type="number"]');
//           if (qtyInput) qtyInput.value = item.quantity;

//           const priceEl = cartItemEl.querySelector('.cart-item-price');
//           if (priceEl) priceEl.textContent =
//             (item.final_line_price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
//         } else {
//           //Append new item using the same markup as your template
//           const newItemHtml = `
//             <div class="cart-item" data-item-key="${item.key}">
//               <div class="cart-item-image" style="width: 33.33%">
//                 <img src="${item.image}" alt="${item.title}">
//               </div>
//               <div class="cart-item-details">
//                 <h4 class="cart-item-title">${item.product_title}</h4>
//                 <p class="cart-item-variant">${item.variant_title}</p>
//                 <p class="cart-item-price">${(item.final_line_price / 100).toLocaleString('en-US', { style:'currency', currency:'USD' })}</p>
//                 <div class="cart-item-quantity">
//                   <label for="quantity-${item.key}">Qty</label>
//                   <input type="number" id="quantity-${item.key}" name="updates[]" value="${item.quantity}" min="0" data-item-key="${item.key}">
//                 </div>
//                 <button class="remove-item" data-item-key="${item.key}">Remove</button>
//               </div>
//             </div>
//           `;
//           itemsContainer.insertAdjacentHTML('beforeend', newItemHtml);
//         }
//       });

//       //Update subtotal
//       if (cartFooter) {
//         const subtotalEl = cartFooter.querySelector('.sz-4:last-child');
//         if (subtotalEl) {
//           subtotalEl.textContent =
//             (cart.total_price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
//         }

//         //Toggle footer visibility
//         cartFooter.classList.toggle('visibility-hidden', cart.item_count === 0);
//       }

//       openCart();
//     })
//     .catch(err => console.error(err));
//   });
// });








/**
 * Common utility function to check for desktop view
 */
function isDesktopView() {
  // Matches SCSS variable $breakpoint-md
  return window.innerWidth > 1024;
}

/**
 * Header Scroll Effects
 * Handles background/foreground color changes based on scroll position
 */
class HeaderScrollEffects {
  constructor(options = {}) {
    this.options = {
      backgroundColor: options.backgroundColor || 'transparent',
      color: options.color || 'white',
      backgroundChangeTo: options.backgroundChangeTo || 'white',
      foregroundChangeTo: options.foregroundChangeTo || '#646b6b',
      changeAt: options.changeAt || 50,
      doScrollChange: options.doScrollChange !== false, // Default true
      ...options
    };

    this.currentBgColor = this.options.backgroundColor;
    this.currentFgColor = this.options.color;
    this.isMenuExpanded = false;
    this.timerToCancel = null;

    this.initElements();
    this.bindEvents();
    this.handleScroll(); // Initial call
  }

  /**
   * Initialize DOM elements
   */
  initElements() {
    this.elements = {
      header: document.querySelector('.app-header'),
      appMenu: document.querySelector('.app-menu'),
      logo: document.querySelector('.logo svg path'),
      searchIcons: document.querySelectorAll('.search svg path'),
      menuLinks: document.querySelectorAll('.menu-link'),
      cartLink: document.querySelector('.cart a'),
      searchInput: document.querySelector('.search-input')
    };
  }

  /**
 * Bind scroll event listener
 */
  bindEvents() {
    window.addEventListener('scroll', () => this.handleScroll());

    // Listen for mobile menu events
    document.addEventListener('menuOpened', () => {
      this.isMenuExpanded = true;
      // Force "scrolled" colors when menu opens (if background is transparent)
      if (this.options.backgroundColor === 'transparent') {
        this.currentBgColor = this.options.backgroundChangeTo;
        this.currentFgColor = this.options.foregroundChangeTo;
        this.updateColors();
      }
    });

    document.addEventListener('menuClosed', () => {
      this.isMenuExpanded = false;
      // Only reset colors if background was transparent
      if (this.options.backgroundColor === 'transparent') {
        // Cancel any existing timer
        if (this.timerToCancel) {
          clearTimeout(this.timerToCancel);
        }
        // Delay to allow menu animation to complete, then re-evaluate scroll
        this.timerToCancel = setTimeout(() => this.handleScroll(), 300);
      }
    });
  }

  /**
   * Handle scroll event and update colors
   */
  handleScroll() {
    if (!this.options.doScrollChange) return;

    // Don't change colors if mobile menu is expanded
    if (this.isMenuExpanded) return;

    const scrollY = window.scrollY;
    if (scrollY >= this.options.changeAt) {
      this.currentBgColor = this.options.backgroundChangeTo;
      this.currentFgColor = this.options.foregroundChangeTo;
      document.documentElement.style.setProperty('--menubg', 'white');

    } else {
      this.currentBgColor = this.options.backgroundColor;
      this.currentFgColor = this.options.color;
      document.documentElement.style.setProperty('--menubg', 'transparent');
    }

    this.updateColors();
  }

  /**
   * Update header colors
   */
  updateColors() {
    // Update header background and text color
    if (this.elements.header) {
      this.elements.header.style.backgroundColor = this.currentBgColor;
      this.elements.header.style.color = this.currentFgColor;
    }

    // Update app menu background
    if (this.elements.appMenu) {
      this.elements.appMenu.style.backgroundColor = this.currentBgColor;
      this.elements.appMenu.style.color = this.currentFgColor;
    }

    // Update logo color
    if (this.elements.logo) {
      this.elements.logo.style.fill = this.currentFgColor;
    }

    // Update search icons
    this.elements.searchIcons.forEach(icon => {
      icon.style.fill = this.currentFgColor;
    });

    // Update menu links
    this.elements.menuLinks.forEach(link => {
      link.style.color = this.currentFgColor;
    });

    // Update cart link
    if (this.elements.cartLink) {
      this.elements.cartLink.style.color = this.currentFgColor;
    }

    // Update search input
    if (this.elements.searchInput) {
      this.elements.searchInput.style.color = this.currentFgColor;
      this.elements.searchInput.style.borderColor = this.currentFgColor;
    }

    // Update hamburger lines (mobile)
    const hamburgerLines = document.querySelectorAll('.hamburger .line');
    hamburgerLines.forEach(line => {
      line.style.backgroundColor = this.currentFgColor;
    });
  }



  /**
   * Update configuration
   */
  updateOptions(newOptions) {
    this.options = { ...this.options, ...newOptions };
    this.handleScroll(); // Re-evaluate with new options
  }

  /**
   * Get current colors
   */
  getCurrentColors() {
    return {
      backgroundColor: this.currentBgColor,
      foregroundColor: this.currentFgColor
    };
  }
}

// /**
//  * Mobile Hamburger Menu Functionality
//  * Handles mobile menu toggle, animations, and scroll management
//  */
// class MobileMenu {
//   constructor() {
//     this.expanded = false;
//     this.scrollPosition = 0;

//     this.initElements();
//     this.bindEvents();
//   }

//   /**
//    * Initialize DOM element references
//    */
//   initElements() {
//     this.elements = {
//       header: document.querySelector('.app-header'),
//       hamburger: document.querySelector('.hamburger'),
//       appMenu: document.querySelector('.app-menu'),
//       body: document.body
//     };

//     // Validate required elements
//     Object.entries(this.elements).forEach(([key, element]) => {
//       if (!element && key !== 'body') {
//         console.warn(`Mobile Menu: Missing required element: ${key}`);
//       }
//     });
//   }

//   /**
//    * Bind event listeners
//    */
//   bindEvents() {
//     // Hamburger click to toggle menu
//     if (this.elements.hamburger) {
//       this.elements.hamburger.addEventListener('click', (e) => {
//         e.preventDefault();
//         this.toggleMenu();
//       });
//     }

//     // Close menu when clicking outside (on mobile overlay)
//     document.addEventListener('click', (e) => {
//       if (this.expanded && this.isMobile()) {
//         // Check if click is outside menu area
//         const menuArea = this.elements.appMenu;
//         if (menuArea && !menuArea.contains(e.target) && !this.elements.hamburger.contains(e.target)) {
//           this.closeMenu();
//         }
//       }
//     });

//     // Close menu on escape key
//     document.addEventListener('keydown', (e) => {
//       if (e.key === 'Escape' && this.expanded) {
//         this.closeMenu();
//       }
//     });

//     // Handle window resize
//     window.addEventListener('resize', () => {
//       // Close menu if switching to desktop
//       if (isDesktopView() && this.expanded) {
//         this.closeMenu();
//       }
//     });

//     // Handle route changes (for SPAs or page navigation)
//     this.handleRouteChanges();
//   }

//   /**
//    * Toggle menu open/close
//    */
//   toggleMenu() {
//     if (this.expanded) {
//       this.closeMenu();
//     } else {
//       this.openMenu();
//     }
//   }

//   /**
//    * Open mobile menu
//    */
//   openMenu() {
//     if (isDesktopView()) return;

//     this.expanded = true;

//     // Add expanded class to header
//     if (this.elements.header) {
//       this.elements.header.classList.add('expanded');
//     }

//     // Add expanded class to menu
//     if (this.elements.appMenu) {
//       this.elements.appMenu.classList.add('overflow-mobile');
//     }

//     // Disable body scroll
//     this.disableScroll();

//     // Close any open dropdown menus
//     if (window.menuHover) {
//       window.menuHover.closeAllMenus();
//     }

//     // Trigger custom event (scroll effects will handle color changes)
//     this.dispatchEvent('menuOpened');
//   }

//   /**
//    * Close mobile menu
//    */
//   closeMenu() {
//     this.expanded = false;

//     // Remove expanded class from header
//     if (this.elements.header) {
//       this.elements.header.classList.remove('expanded');
//     }

//     // Remove expanded class from menu
//     if (this.elements.appMenu) {
//       this.elements.appMenu.classList.remove('overflow-mobile');
//     }

//     // Enable body scroll
//     this.enableScroll();

//     // Trigger custom event
//     this.dispatchEvent('menuClosed');
//   }

//   /**
//    * Disable body scrolling and preserve position
//    */
//   disableScroll() {
//     // Store current scroll position
//     this.scrollPosition = window.scrollY;

//     // Set data attribute for scroll position
//     this.elements.body.setAttribute('data-scroll', this.scrollPosition);

//     // Add class for CSS styling
//     this.elements.body.classList.add('menu-expanded');

//     // Fix body position
//     this.elements.body.style.top = `-${this.scrollPosition}px`;
//     this.elements.body.style.position = 'fixed';
//     this.elements.body.style.width = '100%';
//   }

//   /**
//    * Enable body scrolling and restore position
//    */
//   enableScroll() {
//     // Remove CSS styling
//     this.elements.body.classList.remove('menu-expanded');

//     // Get stored scroll position
//     const scrollY = parseInt(this.elements.body.getAttribute('data-scroll') || '0');

//     // Reset body styles
//     this.elements.body.style.top = '';
//     this.elements.body.style.position = '';
//     this.elements.body.style.width = '';

//     // Restore scroll position
//     window.scrollTo(0, scrollY);

//     // Clean up data attribute
//     this.elements.body.removeAttribute('data-scroll');
//   }

//   /**
//    * Check if current viewport is mobile
//    */
//   isMobile() {
//     return !isDesktopView();
//   }

//   /**
//    * Handle route changes to close menu
//    */
//   handleRouteChanges() {
//     // For hash-based routing
//     window.addEventListener('hashchange', () => {
//       if (this.expanded) {
//         this.closeMenu();
//       }
//     });

//     // For regular page navigation
//     window.addEventListener('beforeunload', () => {
//       if (this.expanded) {
//         this.enableScroll(); // Ensure scroll is restored
//       }
//     });

//     // For SPA navigation (if using History API)
//     window.addEventListener('popstate', () => {
//       if (this.expanded) {
//         this.closeMenu();
//       }
//     });

//     // Listen for link clicks to close menu
//     document.addEventListener('click', (e) => {
//       const link = e.target.closest('a[href]');
//       if (link && this.expanded) {
//         // Close menu when navigating
//         setTimeout(() => this.closeMenu(), 100);
//       }
//     });
//   }

//   /**
//    * Dispatch custom events
//    */
//   dispatchEvent(eventName) {
//     const event = new CustomEvent(eventName, {
//       detail: { expanded: this.expanded },
//       bubbles: true
//     });
//     document.dispatchEvent(event);
//   }

//   /**
//    * Get current menu state
//    */
//   isExpanded() {
//     return this.expanded;
//   }

//   /**
//    * Force close menu (public method)
//    */
//   close() {
//     this.closeMenu();
//   }

//   /**
//    * Force open menu (public method)
//    */
//   open() {
//     this.openMenu();
//   }

//   /**
//    * Destroy the mobile menu (cleanup)
//    */
//   destroy() {
//     // Ensure menu is closed
//     if (this.expanded) {
//       this.closeMenu();
//     }

//     // Remove event listeners would go here if needed
//     // (In this implementation, we're using mostly document/window listeners)
//   }
// }

// /**
//  * Menu Hover Functionality
//  * Handles expanding/collapsing dropdown menus on hover (desktop) and click (mobile)
//  */
// class MenuHover {
//   constructor() {
//     this.currentOpenMenu = null;
//     this.debounceTimeout = null;
//     this.initElements();
//     this.bindEvents();
//   }

//   /**
//    * Initialize DOM element references
//    */
//   initElements() {
//     this.menuItems = document.querySelectorAll('.menu-item');
//     this.menuItemsWithChildren = [];

//     // Find menu items that have sub-menus
//     this.menuItems.forEach(menuItem => {
//       const subMenu = menuItem.querySelector('.sub-menu');
//       if (subMenu) {
//         this.menuItemsWithChildren.push({
//           element: menuItem,
//           subMenu,
//           uid: this.generateUID()
//         });
//       }
//     });
//   }

//   /**
//    * Generate unique identifier for menu items
//    */
//   generateUID() {
//     return '_' + Math.random().toString(36).substr(2, 9);
//   }

//   /**
//    * Bind event listeners
//    */
//   bindEvents() {
//     this.menuItemsWithChildren.forEach(menuData => {
//       const { element } = menuData;

//       // Desktop hover behavior
//       element.addEventListener('mouseenter', () => this.expandMenu(menuData));
//       element.addEventListener('mouseleave', () => this.closeMenu(menuData));

//       // Mobile click behavior
//       const menuLink = element.querySelector('.menu-link');
//       if (menuLink && !menuLink.href && !menuLink.closest('a[href]')) {
//         menuLink.addEventListener('click', (e) => {
//           e.preventDefault();
//           this.toggleMenu(menuData);
//         });
//       }
//     });

//          // Close all menus when clicking outside
//      document.addEventListener('click', (e) => {
//        const clickedInsideMenu = e.target.closest('.menu-item');
//        if (!clickedInsideMenu && this.currentOpenMenu) {
//          this.contractAll();
//        }
//      });

//      // Close dropdown menus when submenu links are clicked (mobile)
//      this.menuItemsWithChildren.forEach(menuData => {
//        const subMenuLinks = menuData.subMenu.querySelectorAll('.menu-link[href], a.menu-link');
//        subMenuLinks.forEach(link => {
//          link.addEventListener('click', () => {
//            if (!isDesktopView()) {
//              this.contractAll();
//            }
//          });
//        });
//      });

//     // Handle window resize
//     window.addEventListener('resize', () => {
//       if (isDesktopView() && this.currentOpenMenu) {
//         // Keep menu open on desktop
//       } else if (!isDesktopView()) {
//         // Close all menus when switching to mobile
//         this.contractAll();
//       }
//     });
//   }

//   /**
//    * Expand menu with debounce (desktop hover)
//    */
//   expandMenu(menuData) {
//     if (!isDesktopView()) return;

//     // Clear any existing timeout
//     if (this.debounceTimeout) {
//       clearTimeout(this.debounceTimeout);
//     }

//     this.debounceTimeout = setTimeout(() => {
//       this.debounceTimeout = null;
//       this.expand(menuData);
//     }, 250);
//   }

//   /**
//    * Close menu with debounce (desktop hover leave)
//    */
//   closeMenu(menuData) {
//     if (!isDesktopView()) return;

//     // Clear any existing timeout
//     if (this.debounceTimeout) {
//       clearTimeout(this.debounceTimeout);
//     }

//     this.debounceTimeout = setTimeout(() => {
//       this.debounceTimeout = null;
//       this.contract();
//     }, 250);
//   }

//   /**
//    * Toggle menu (mobile click)
//    */
//   toggleMenu(menuData) {
//     if (isDesktopView()) return;

//     if (this.currentOpenMenu === menuData.uid) {
//       this.contract();
//     } else {
//       this.expand(menuData);
//     }
//   }

//   /**
//    * Actually expand the menu
//    */
//   expand(menuData) {
//     // Close currently open menu if different
//     if (this.currentOpenMenu && this.currentOpenMenu !== menuData.uid) {
//       this.contractAll();
//     }

//     // Close search if open
//     if (window.searchFunctionality && window.searchFunctionality.isExpanded()) {
//       window.searchFunctionality.closeSearch();
//     }

//     // Open this menu
//     this.currentOpenMenu = menuData.uid;
//     menuData.subMenu.classList.add('expanded');

//     // Update colors for menu links in submenu
//     this.updateSubMenuColors(menuData.subMenu);

//     this.dispatchEvent('menuExpanded', menuData);
//   }

//   /**
//    * Contract the currently open menu
//    */
//   contract() {
//     if (!this.currentOpenMenu) return;

//     const openMenuData = this.menuItemsWithChildren.find(m => m.uid === this.currentOpenMenu);
//     if (openMenuData) {
//       openMenuData.subMenu.classList.remove('expanded');
//     }

//     this.currentOpenMenu = null;
//     this.dispatchEvent('menuContracted');
//   }

//   /**
//    * Contract all menus
//    */
//   contractAll() {
//     this.menuItemsWithChildren.forEach(menuData => {
//       menuData.subMenu.classList.remove('expanded');
//     });
//     this.currentOpenMenu = null;
//     this.dispatchEvent('allMenusContracted');
//   }

//   /**
//    * Update submenu colors to match current header colors
//    */
//   updateSubMenuColors(subMenu) {
//     if (window.headerScrollEffects) {
//       const colors = window.headerScrollEffects.getCurrentColors();
//       const menuLinks = subMenu.querySelectorAll('.menu-link');

//       menuLinks.forEach(link => {
//         link.style.color = colors.foregroundColor;
//       });

//       // Update CSS custom property for background
//       subMenu.style.setProperty('--menubg', colors.backgroundColor);
//       subMenu.style.setProperty('--bottomline', 
//         colors.backgroundColor === 'transparent' ? '' : '#eee'
//       );
//     }
//   }

//   /**
//    * Get currently expanded menu
//    */
//   getCurrentOpenMenu() {
//     return this.currentOpenMenu;
//   }

//   /**
//    * Check if any menu is expanded
//    */
//   isAnyMenuExpanded() {
//     return this.currentOpenMenu !== null;
//   }

//   /**
//    * Dispatch custom events
//    */
//   dispatchEvent(eventName, menuData = null) {
//     const event = new CustomEvent(eventName, {
//       detail: { 
//         menuId: this.currentOpenMenu,
//         menuData,
//         isExpanded: this.isAnyMenuExpanded()
//       },
//       bubbles: true
//     });
//     document.dispatchEvent(event);
//   }

//   /**
//    * Force close all menus (public method)
//    */
//   closeAllMenus() {
//     if (this.debounceTimeout) {
//       clearTimeout(this.debounceTimeout);
//       this.debounceTimeout = null;
//     }
//     this.contractAll();
//   }
// }

/**
 * Search Functionality
 * Handles expanding/collapsing the search input and form submission.
 */
class SearchFunctionality {
  constructor() {
    this.expanded = false;
    this.initElements();
    if (this.elements.searchIcon) {
      this.bindEvents();
    }
  }

  /**
   * Initialize DOM element references
   */
  initElements() {
    this.elements = {
      searchContainer: document.querySelector('.menu-item.search'),
      searchIcon: document.querySelector('.menu-item-label.search'),
      searchForm: document.querySelector('form.searchForm'),
      searchInput: document.querySelector('input.search-input'),
      // Exclude both search and cart items so cart stays visible when search opens
      menuItems: document.querySelectorAll('.menu.right-menu .menu-item:not(.search):not(.cart-item)')
    };



    // DEBUG: Check if cart-item exists and what class it has
    const cartItem = document.querySelector('.cart-item');
    const allRightMenuItems = document.querySelectorAll('.menu.right-menu .menu-item');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    this.elements.searchIcon.addEventListener('click', (e) => this.handleIconClick(e));
    this.elements.searchInput.addEventListener('blur', () => this.closeSearch());
    this.elements.searchForm.addEventListener('submit', (e) => this.handleSubmit(e));
    this.elements.searchInput.addEventListener('click', (e) => e.stopPropagation()); // Prevent blur when clicking inside input
  }

  /**
   * Handle click on the search icon
   */
  handleIconClick(e) {
    e.stopPropagation(); // Prevent triggering other click listeners
    if (this.expanded && this.elements.searchInput.value.length > 0) {
      this.elements.searchForm.submit();
    } else {
      this.toggleSearch();
    }
  }

  /**
   * Toggle search input visibility
   */
  toggleSearch() {
    if (this.expanded) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  /**
   * Expand the search input
   */
  openSearch() {
    if (this.expanded) return;
    this.expanded = true;
    this.elements.searchIcon.classList.add('expanded');

    // Close any open dropdown menus
    if (window.menuHover) {
      window.menuHover.closeAllMenus();
    }

    // Hide other menu items on desktop
    if (isDesktopView()) {
      this.elements.menuItems.forEach((item, i) => {
        item.classList.add('expanded');
      });
    }

    this.elements.searchInput.focus();
    this.dispatchEvent('searchOpened');
  }

  /**
   * Collapse the search input
   */
  closeSearch() {
    if (!this.expanded) return;
    // Don't close if there's text, user might be tabbing
    if (document.activeElement === this.elements.searchInput) {
      return;
    }
    this.expanded = false;
    this.elements.searchIcon.classList.remove('expanded');

    // Show other menu items on desktop
    if (isDesktopView()) {
      this.elements.menuItems.forEach(item => item.classList.remove('expanded'));
    }

    this.dispatchEvent('searchClosed');
  }

  /**
   * Handle form submission
   */
  handleSubmit(e) {
    if (this.elements.searchInput.value.length === 0) {
      e.preventDefault(); // Prevent submitting empty form
      this.closeSearch();
    }
  }

  /**
   * Check if search is expanded
   */
  isExpanded() {
    return this.expanded;
  }

  /**
   * Dispatch custom events
   */
  dispatchEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: { expanded: this.expanded },
      bubbles: true
    });
    document.dispatchEvent(event);
  }
}

/**
 * Announcement Banner Functionality
 * Handles reading announcement banner height and updating CSS custom property
 */
class AnnouncementBanner {
  constructor() {
    this.announcementElement = null;
    this.height = 0;
    this.initElements();
    this.bindEvents();
    this.updateHeight(); // Initial call
  }

  /**
   * Initialize DOM element references
   */
  initElements() {
    this.announcementElement = document.querySelector('.app-announcement');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Update height on window resize
    window.addEventListener('resize', () => {
      this.updateHeight();
    });

    // Listen for DOM changes that might affect the announcement banner
    const observer = new MutationObserver(() => {
      this.updateHeight();
    });

    // Observe the body for changes that might affect the announcement banner
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }
  }

  /**
   * Update the announcement banner height and CSS custom property
   */
  updateHeight() {
    if (!this.announcementElement) {
      // Try to find the element again in case it was added dynamically
      this.announcementElement = document.querySelector('.app-announcement');
    }

    if (this.announcementElement) {
      // Get the current height
      const newHeight = this.announcementElement.clientHeight || 0;

      // Only update if height has changed
      if (newHeight !== this.height) {
        this.height = newHeight;

        // Update CSS custom property
        document.documentElement.style.setProperty('--announcement-height', `${this.height}px`);

        // Also set on body for backward compatibility
        document.body.style.setProperty('--announceHeight', `${this.height}px`);

        this.dispatchEvent('heightUpdated', { height: this.height });
      }
    } else if (this.height !== 0) {
      // No announcement banner found, set height to 0
      this.height = 0;
      document.documentElement.style.setProperty('--announcement-height', '0px');
      document.body.style.setProperty('--announceHeight', '0px');
      this.dispatchEvent('heightUpdated', { height: 0 });
    }
  }

  /**
   * Get current announcement banner height
   */
  getHeight() {
    return this.height;
  }

  /**
   * Check if announcement banner is visible
   */
  isVisible() {
    return this.announcementElement &&
      this.announcementElement.style.display !== 'none' &&
      this.announcementElement.style.visibility !== 'hidden' &&
      this.height > 0;
  }

  /**
   * Dispatch custom events
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: {
        height: this.height,
        isVisible: this.isVisible(),
        ...detail
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Force update height (public method)
   */
  refresh() {
    this.updateHeight();
  }

  /**
   * Destroy the announcement banner functionality (cleanup)
   */
  destroy() {
    // Remove any event listeners if needed
    // The MutationObserver will be automatically cleaned up
  }
}

/**
 * Initialize header functionality when DOM is ready
 */
function initializeHeader() {
  // Initialize mobile menu
  // const mobileMenu = new MobileMenu();
  const searchFunctionality = new SearchFunctionality();
  // const menuHover = new MenuHover();
  const announcementBanner = new AnnouncementBanner();

  // const isHeaderTransparent = document.body.classList.contains('variable-header');
  const isHeaderTransparent = document.querySelector('.header-dark-theme');

  // Initialize scroll effects
  const scrollEffects = new HeaderScrollEffects({
    backgroundColor: isHeaderTransparent ? 'transparent' : 'white',
    color: isHeaderTransparent ? 'white' : '#646b6b',
    backgroundChangeTo: 'white',
    foregroundChangeTo: '#646b6b',
    changeAt: 10,
    doScrollChange: isHeaderTransparent
  });

  // Make globally accessible
  // window.mobileMenu = mobileMenu;
  // window.searchFunctionality = searchFunctionality;
  // window.menuHover = menuHover;
  window.headerScrollEffects = scrollEffects;
  window.announcementBanner = announcementBanner;

  // Global functions for easy access
  // window.toggleMobileMenu = () => mobileMenu.toggleMenu();
  // window.closeMobileMenu = () => mobileMenu.close();
  // window.openMobileMenu = () => mobileMenu.open();

  // Global functions for scroll effects
  window.updateHeaderColors = (options) => scrollEffects.updateOptions(options);
  window.getHeaderColors = () => scrollEffects.getCurrentColors();

  // Global functions for menu hover
  // window.closeAllMenus = () => menuHover.closeAllMenus();
  window.refreshAnnouncementBanner = () => announcementBanner.refresh();

  return { scrollEffects, announcementBanner };
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializeHeader);

// Fallback initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHeader);
} else {
  // DOM already loaded
  initializeHeader();
}


// search results product media 



// (function() {
//   // Function to apply high-res images
//   function applyHighResImages(root = document) {
//     // Select all main images that have a high-res URL and are not already applied
//     const imgs = root.querySelectorAll('.product-media__image[data_max_resolution]:not([data-highres-applied])');

//     imgs.forEach(img => {
//       // Skip if it's a variant swap / swatch
//       if (img.dataset.noVariantSwap === 'false') return;

//       let highRes = img.getAttribute('data_max_resolution');
//       if (!highRes) return;

//       // Ensure https
//       highRes = highRes.startsWith('//') ? 'https:' + highRes : highRes;

//       // Preload high-res image
//       const tmp = new Image();
//       tmp.onload = () => {
//         img.src = highRes;
//         img.srcset = highRes;
//         img.sizes = '100vw';
//         img.dataset.srcset = highRes; // for theme JS fallback
//         img.loading = 'eager';
//         img.dataset.highresApplied = 'true';
//         console.log('High-res applied:', highRes);
//       };
//       tmp.onerror = () => console.warn('Failed to load high-res:', highRes);
//       tmp.src = highRes;
//     });
//   }

//   // Initial run
//   applyHighResImages();

//   // Observe dynamically added product cards
//   const resultsList = document.querySelector('results-list') || document.body;
//   const observer = new MutationObserver(mutations => {
//     mutations.forEach(m => {
//       if (m.addedNodes.length) applyHighResImages(m.target);
//     });
//   });
//   observer.observe(resultsList, { childList: true, subtree: true });

//   // Hook into variant change events (Horizon/Dawn)
//   document.addEventListener('variant:change', e => {
//     const productCard = e.target.closest('.product-card') || document;
//     applyHighResImages(productCard);
//   });

//   // Fallback interval for late-loaded images
//   const interval = setInterval(() => {
//     const pending = document.querySelector('.product-media__image[data_max_resolution]:not([data-highres-applied])');
//     if (pending) {
//       applyHighResImages();
//     } else {
//       clearInterval(interval);
//     }
//   }, 500);
// })();


document.addEventListener("DOMContentLoaded", function () {
  const productJsonEl = document.querySelector('[type="application/json"][data-product-json]');
  if (!productJsonEl) return;
  const productData = JSON.parse(productJsonEl.textContent);

  // Store original variant images
  const originalVariants = JSON.parse(JSON.stringify(productData.variants));

  // Listen for Swatch King / Horizon variant selection
  document.addEventListener('variantChange', function (e) {
    const variant = e.detail.variant;
    if (!variant) return;

    // Find the corresponding original variant
    const originalVariant = originalVariants.find(v => v.id === variant.id);
    if (!originalVariant) return;

    // Check metafield image
    if (originalVariant.metafields?.custom?.fabric_image?.length) {
      // Override the variant's featured_image to our metafield
      variant.featured_image = {
        src: originalVariant.metafields.custom.fabric_image[0],
        alt: originalVariant.title
      };
    }

    // If no metafield, let Horizon use variant.featured_image (default)
    // If no variant image, Horizon uses product.images[0] automatically
  });
});
